<script setup lang="ts">
/**
 * Building a menu (SPEC §6.16, §6.19, §8.14, §8.16, §13.5).
 *
 * The same editor for a meal template and for an event menu. The two differ in
 * what surrounds an item — an event's items carry what families have already
 * promised — so that goes in a slot rather than in a second copy of this file.
 *
 * Reordering works two ways on purpose. Drag is what most people reach for,
 * and it is unusable with a keyboard, a switch, or a screen reader; so the move
 * buttons are the real implementation and drag is the convenience layered on
 * top. Every move announces itself in a live region, because somebody who
 * cannot see the list move needs to be told that it did.
 */
import { computed, ref } from 'vue'
import {
  emptyItem,
  emptySection,
  moved,
  participationModes,
  type MenuItemDraft,
  type MenuSectionDraft,
} from './menuEditor'

const props = withDefaults(
  defineProps<{
    modelValue: MenuSectionDraft[]
    disabled?: boolean
    /** What to call the whole thing, for the empty state and the add button. */
    noun?: string
  }>(),
  { disabled: false, noun: 'menu' },
)

const emit = defineEmits<{ 'update:modelValue': [MenuSectionDraft[]] }>()

/**
 * What just happened, for anybody not watching the list.
 *
 * Cleared and re-set rather than appended, so a screen reader reads the latest
 * move rather than a growing history of them.
 */
const announcement = ref('')

/** Which item is open for detailed editing, keyed by section and position. */
const expanded = ref<string | null>(null)

const sections = computed(() => props.modelValue)

const isEmpty = computed(() => sections.value.length === 0)

function announce(message: string) {
  announcement.value = message
}

function replace(next: MenuSectionDraft[]) {
  emit('update:modelValue', next)
}

function updateSection(index: number, change: Partial<MenuSectionDraft>) {
  replace(sections.value.map((s, i) => (i === index ? { ...s, ...change } : s)))
}

function updateItem(sectionIndex: number, itemIndex: number, change: Partial<MenuItemDraft>) {
  const section = sections.value[sectionIndex]

  updateSection(sectionIndex, {
    items: section.items.map((item, i) => (i === itemIndex ? { ...item, ...change } : item)),
  })
}

function addSection() {
  replace([...sections.value, emptySection()])
  announce('Added a section.')
}

function removeSection(index: number) {
  const name = sections.value[index].name || 'the section'
  replace(sections.value.filter((_, i) => i !== index))
  announce(`Removed ${name}.`)
}

function moveSection(index: number, offset: number) {
  const next = moved(sections.value, index, offset)

  if (next === sections.value) return

  replace(next)

  const name = sections.value[index].name || 'Section'
  announce(`Moved ${name} to position ${index + offset + 1} of ${next.length}.`)
}

function addItem(sectionIndex: number) {
  const section = sections.value[sectionIndex]

  updateSection(sectionIndex, { items: [...section.items, emptyItem()] })
  announce(`Added an item to ${section.name || 'the section'}.`)
}

function removeItem(sectionIndex: number, itemIndex: number) {
  const section = sections.value[sectionIndex]
  const name = section.items[itemIndex].name || 'the item'

  updateSection(sectionIndex, { items: section.items.filter((_, i) => i !== itemIndex) })
  announce(`Removed ${name}.`)
}

function moveItem(sectionIndex: number, itemIndex: number, offset: number) {
  const section = sections.value[sectionIndex]
  const next = moved(section.items, itemIndex, offset)

  if (next === section.items) return

  updateSection(sectionIndex, { items: next })

  const name = section.items[itemIndex].name || 'Item'
  announce(`Moved ${name} to position ${itemIndex + offset + 1} of ${next.length}.`)
}

/**
 * Whether a quantity means anything for this item.
 *
 * The same rule the domain enforces. Showing the fields for a host-provided
 * dish would let somebody fill them in and then wonder where the numbers went
 * (SPEC §6.16).
 */
function asksForHelp(item: MenuItemDraft): boolean {
  return item.participationMode === 'SignupRequired'
}

function toggleDetail(key: string) {
  expanded.value = expanded.value === key ? null : key
}

// --- drag, for the people who reach for it -------------------------------
const dragging = ref<{ section: number; item: number | null } | null>(null)

function startDrag(section: number, item: number | null) {
  dragging.value = { section, item }
}

function dropOnSection(target: number) {
  const from = dragging.value

  dragging.value = null

  if (!from || from.item !== null || from.section === target) return

  moveSection(from.section, target - from.section)
}

function dropOnItem(sectionIndex: number, target: number) {
  const from = dragging.value

  dragging.value = null

  // Only within a section. Dragging an item between sections is a different
  // gesture with a different meaning, and getting it wrong silently moves
  // somebody's dessert into the starters.
  if (!from || from.item === null || from.section !== sectionIndex || from.item === target) return

  moveItem(sectionIndex, from.item, target - from.item)
}
</script>

<template>
  <div class="menu-editor">
    <p aria-live="polite" class="menu-editor__announcement">{{ announcement }}</p>

    <p v-if="isEmpty" class="menu-editor__empty">
      Nothing on the {{ noun }} yet. Start with a heading — MAIN, SIDES, DESSERT — and put things
      under it.
    </p>

    <section
      v-for="(section, sectionIndex) in sections"
      :key="section.id ?? `new-section-${sectionIndex}`"
      class="menu-editor__section"
      :draggable="!disabled"
      @dragstart="startDrag(sectionIndex, null)"
      @dragover.prevent
      @drop.prevent="dropOnSection(sectionIndex)"
    >
      <header class="menu-editor__section-head">
        <v-text-field
          :model-value="section.name"
          :disabled="disabled"
          :label="`Section ${sectionIndex + 1} name`"
          density="compact"
          variant="outlined"
          hide-details
          class="menu-editor__section-name"
          @update:model-value="updateSection(sectionIndex, { name: $event })"
        />

        <div class="menu-editor__controls">
          <v-btn
            :disabled="disabled || sectionIndex === 0"
            :aria-label="`Move ${section.name || 'section'} up`"
            icon="mdi-arrow-up"
            variant="text"
            density="comfortable"
            @click="moveSection(sectionIndex, -1)"
          />
          <v-btn
            :disabled="disabled || sectionIndex === sections.length - 1"
            :aria-label="`Move ${section.name || 'section'} down`"
            icon="mdi-arrow-down"
            variant="text"
            density="comfortable"
            @click="moveSection(sectionIndex, 1)"
          />
          <v-btn
            :disabled="disabled"
            :aria-label="`Remove ${section.name || 'section'}`"
            icon="mdi-close"
            variant="text"
            density="comfortable"
            @click="removeSection(sectionIndex)"
          />
        </div>
      </header>

      <ol class="menu-editor__items">
        <li
          v-for="(item, itemIndex) in section.items"
          :key="item.id ?? `new-item-${sectionIndex}-${itemIndex}`"
          class="menu-editor__item"
          :draggable="!disabled"
          @dragstart.stop="startDrag(sectionIndex, itemIndex)"
          @dragover.prevent
          @drop.prevent.stop="dropOnItem(sectionIndex, itemIndex)"
        >
          <div class="menu-editor__item-row">
            <v-text-field
              :model-value="item.name"
              :disabled="disabled"
              :label="`Item ${itemIndex + 1}`"
              density="compact"
              variant="outlined"
              hide-details
              class="menu-editor__item-name"
              @update:model-value="updateItem(sectionIndex, itemIndex, { name: $event })"
            />

            <div class="menu-editor__controls">
              <v-btn
                :disabled="disabled || itemIndex === 0"
                :aria-label="`Move ${item.name || 'item'} up`"
                icon="mdi-arrow-up"
                variant="text"
                density="comfortable"
                @click="moveItem(sectionIndex, itemIndex, -1)"
              />
              <v-btn
                :disabled="disabled || itemIndex === section.items.length - 1"
                :aria-label="`Move ${item.name || 'item'} down`"
                icon="mdi-arrow-down"
                variant="text"
                density="comfortable"
                @click="moveItem(sectionIndex, itemIndex, 1)"
              />
              <v-btn
                :aria-label="`Details for ${item.name || 'item'}`"
                :aria-expanded="expanded === `${sectionIndex}-${itemIndex}`"
                icon="mdi-tune"
                variant="text"
                density="comfortable"
                @click="toggleDetail(`${sectionIndex}-${itemIndex}`)"
              />
              <v-btn
                :disabled="disabled"
                :aria-label="`Remove ${item.name || 'item'}`"
                icon="mdi-close"
                variant="text"
                density="comfortable"
                @click="removeItem(sectionIndex, itemIndex)"
              />
            </div>
          </div>

          <!--
            Whatever the screen using this wants to say about the item. An
            event menu puts what families have already promised here; a
            template has nothing to add.
          -->
          <slot name="item-meta" :item="item" :section="section" />

          <!--
            Built on demand rather than hidden. A thirty-item menu would
            otherwise render thirty complete forms, and every one of them
            would be in the tab order and read out by a screen reader.
          -->
          <div v-if="expanded === `${sectionIndex}-${itemIndex}`" class="menu-editor__detail">
            <v-select
              :model-value="item.participationMode"
              :items="participationModes"
              :disabled="disabled"
              item-title="title"
              item-value="value"
              label="Who brings it"
              density="compact"
              variant="outlined"
              hide-details
              @update:model-value="
                updateItem(sectionIndex, itemIndex, { participationMode: $event })
              "
            />

            <p class="menu-editor__hint">
              {{ participationModes.find((m) => m.value === item.participationMode)?.description }}
            </p>

            <v-textarea
              :model-value="item.notes ?? ''"
              :disabled="disabled"
              label="Notes"
              placeholder="Enough for 12. Nothing with nuts."
              rows="2"
              density="compact"
              variant="outlined"
              hide-details
              @update:model-value="updateItem(sectionIndex, itemIndex, { notes: $event || null })"
            />

            <template v-if="asksForHelp(item)">
              <div class="menu-editor__quantity">
                <v-text-field
                  :model-value="item.quantityNeeded ?? ''"
                  :disabled="disabled"
                  label="How many"
                  type="number"
                  min="0"
                  step="0.5"
                  density="compact"
                  variant="outlined"
                  hide-details
                  @update:model-value="
                    updateItem(sectionIndex, itemIndex, {
                      quantityNeeded: $event === '' ? null : Number($event),
                    })
                  "
                />
                <v-text-field
                  :model-value="item.unitLabel ?? ''"
                  :disabled="disabled"
                  label="Of what"
                  placeholder="trays, pounds, dozen"
                  density="compact"
                  variant="outlined"
                  hide-details
                  @update:model-value="
                    updateItem(sectionIndex, itemIndex, { unitLabel: $event || null })
                  "
                />
              </div>

              <v-switch
                :model-value="item.isFlexible"
                :disabled="disabled"
                label="Anything of this kind will do"
                color="primary"
                density="compact"
                hide-details
                @update:model-value="
                  updateItem(sectionIndex, itemIndex, { isFlexible: Boolean($event) })
                "
              />

              <v-switch
                :model-value="item.allowOverClaim"
                :disabled="disabled"
                label="More than asked for is fine"
                color="primary"
                density="compact"
                hide-details
                @update:model-value="
                  updateItem(sectionIndex, itemIndex, { allowOverClaim: Boolean($event) })
                "
              />
            </template>
          </div>
        </li>
      </ol>

      <v-btn
        :disabled="disabled"
        variant="text"
        density="comfortable"
        prepend-icon="mdi-plus"
        @click="addItem(sectionIndex)"
      >
        Add something to {{ section.name || 'this section' }}
      </v-btn>
    </section>

    <v-btn
      :disabled="disabled"
      variant="outlined"
      color="primary"
      prepend-icon="mdi-plus"
      class="menu-editor__add-section"
      @click="addSection"
    >
      Add a section
    </v-btn>
  </div>
</template>

<style scoped>
.menu-editor {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/*
  Present to a screen reader, absent to everybody else. display:none would take
  it out of the accessibility tree along with the announcement.
*/
.menu-editor__announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.menu-editor__empty {
  color: rgb(var(--v-theme-on-surface-variant));
  max-width: 48ch;
}

.menu-editor__section {
  border: 1px solid rgb(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.menu-editor__section-head,
.menu-editor__item-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.menu-editor__section-name,
.menu-editor__item-name {
  flex: 1 1 auto;
  min-width: 0;
}

.menu-editor__controls {
  display: flex;
  flex: 0 0 auto;
}

.menu-editor__items {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.menu-editor__item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.menu-editor__detail {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 0 0.25rem;
}

.menu-editor__hint {
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 0.875rem;
  margin: -0.25rem 0 0;
}

.menu-editor__quantity {
  display: flex;
  gap: 0.5rem;
}

.menu-editor__add-section {
  align-self: flex-start;
}

/*
  The controls wrap under the field on a phone rather than squeezing it to
  nothing. Four icon buttons and a text field do not fit across 360px.
*/
@media (max-width: 30rem) {
  .menu-editor__section-head,
  .menu-editor__item-row {
    flex-wrap: wrap;
  }

  .menu-editor__section-name,
  .menu-editor__item-name {
    flex: 1 0 100%;
  }
}
</style>
